/**
 * ═══════════════════════════════════════════════════════════════
 * CUSTOM PRODUCT INTEGRATION
 * ═══════════════════════════════════════════════════════════════
 * Connects subscription checkbox/dropdown to native Shopify form.
 * Does NOT hijack cart submission - lets theme handle it.
 * ═══════════════════════════════════════════════════════════════
 */

class CustomProductSubscription {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.container = document.querySelector(`#cus-custom-subscription-${sectionId}`);
    
    if (!this.container) {
      console.warn(`[CustomProduct] Container not found for section ${sectionId}`);
      return;
    }
    
    // Load product data
    this.data = this.loadData();
    if (!this.data) {
      console.warn(`[CustomProduct] No product data found for section ${sectionId}`);
      return;
    }
    
    // Get form ID and find form
    this.formId = this.data.form_id;
    this.form = document.getElementById(this.formId);
    
    if (!this.form) {
      console.warn(`[CustomProduct] Form not found: ${this.formId}`);
      return;
    }
    
// Get UI elements
    this.checkbox = document.querySelector(`#ph-sub-toggle-${sectionId}`);
    this.dropdown = document.querySelector(`#ph-pack-dropdown-${sectionId}`);
    this.frequencyText = document.querySelector(`#ph-frequency-text-${sectionId}`);
    
    if (!this.checkbox || !this.dropdown) {
      console.warn(`[CustomProduct] UI elements not found`);
      return;
    }
    
    // Find or create selling plan input in native form
    this.setupSellingPlanInput();
    
    // Initialize
    this.init();
    
    console.log(`[CustomProduct] Initialized for section ${sectionId}, form ${this.formId}`);
  }
  
  loadData() {
    const script = document.getElementById(`ph-product-data-${this.sectionId}`);
    if (!script) return null;
    
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[CustomProduct] Failed to parse product data:', e);
      return null;
    }
  }
  
  setupSellingPlanInput() {
    // Look for existing selling plan input
    this.sellingPlanInput = this.form.querySelector('input[name="selling_plan"]');
    
    // Create if doesn't exist
    if (!this.sellingPlanInput) {
      this.sellingPlanInput = document.createElement('input');
      this.sellingPlanInput.type = 'hidden';
      this.sellingPlanInput.name = 'selling_plan';
      this.sellingPlanInput.setAttribute('form', this.formId);
      this.form.appendChild(this.sellingPlanInput);
      console.log('[CustomProduct] Created selling_plan input');
    }
  }
  
  init() {
    this.populateDropdown();
    this.bindEvents();
  }
  
  getSellingPlans() {
    const plans = [];
    const groups = this.data.main_product.selling_plan_groups || [];
    
    groups.forEach(group => {
      if (!group.selling_plans) return;
      
      group.selling_plans.forEach(sp => {
        const name = (sp.name || '').toLowerCase();
        let frequency = null;
        
        // Parse frequency from plan name
        if (name.includes('3 month')) frequency = 3;
        else if (name.includes('4 month')) frequency = 4;
        else if (name.includes('6 month')) frequency = 6;
        
        if (frequency) {
          const discount = (sp.price_adjustments && sp.price_adjustments[0]) 
            ? sp.price_adjustments[0].value 
            : 0;
            
          plans.push({
            id: sp.id,
            frequency: frequency,
            discount: discount
          });
        }
      });
    });
    
    return plans;
  }
  
populateDropdown() {
    this.dropdown.innerHTML = '';
    
    const isSubscription = this.checkbox.checked;
    const hasSellingPlans = this.data.main_product.has_selling_plans > 0;
    
    if (isSubscription && hasSellingPlans) {
      // Show subscription frequencies
      const plans = this.getSellingPlans();
      
      if (plans.length === 1) {
        // Single plan: show as plain text
        const plan = plans[0];
        this.frequencyText.textContent = `Delivery Every ${plan.frequency} Months (${plan.discount}% off)`;
        this.frequencyText.style.display = 'block';
        this.dropdown.style.display = 'none';
        
        // Still need to populate dropdown with the option for form submission
        const option = document.createElement('option');
        option.value = plan.id;
        option.selected = true;
        this.dropdown.appendChild(option);
        
        console.log(`[CustomProduct] Showing single plan as text`);
      } else {
        // Multiple plans: show as dropdown
        this.frequencyText.style.display = 'none';
        this.dropdown.style.display = 'block';
        
        plans.forEach((plan, index) => {
          const option = document.createElement('option');
          option.value = plan.id;
          option.textContent = `Every ${plan.frequency} Months (${plan.discount}% off)`;
          if (index === 0) option.selected = true;
          this.dropdown.appendChild(option);
        });
        
        console.log(`[CustomProduct] Showing ${plans.length} plans as dropdown`);
      }
    } else {
      // One-time purchase: show as plain text
      this.frequencyText.textContent = 'One-time purchase';
      this.frequencyText.style.display = 'block';
      this.dropdown.style.display = 'none';
      
      // Add empty option for one-time
      const option = document.createElement('option');
      option.value = '';
      this.dropdown.appendChild(option);
      
      console.log('[CustomProduct] Showing one-time as text');
    }
    
    this.updateSellingPlanInput();
  }
  
  updateSellingPlanInput() {
    const isSubscription = this.checkbox.checked;
    const selectedValue = this.dropdown.value;
    
    if (isSubscription && selectedValue) {
      this.sellingPlanInput.value = selectedValue;
      console.log(`[CustomProduct] Set selling_plan to: ${selectedValue}`);
    } else {
      this.sellingPlanInput.value = '';
      console.log('[CustomProduct] Cleared selling_plan (one-time purchase)');
    }
    
    // Dispatch change event so theme can react if needed
    this.form.dispatchEvent(new Event('change', { bubbles: true }));
  }
  
  bindEvents() {
    this.checkbox.addEventListener('change', () => {
      console.log(`[CustomProduct] Subscription toggled: ${this.checkbox.checked}`);
      this.populateDropdown();
    });
    
    this.dropdown.addEventListener('change', () => {
      console.log(`[CustomProduct] Frequency changed: ${this.dropdown.value}`);
      this.updateSellingPlanInput();
    });
  }
}

// ═══════════════════════════════════════════════════════════════
// AUTO-INITIALIZE
// ═══════════════════════════════════════════════════════════════

document.addEventListener('DOMContentLoaded', function() {
  console.log('[CustomProduct] Initializing...');
  
  // Find all custom product subscription containers
  const containers = document.querySelectorAll('[id^="cus-custom-subscription-"]');
  
  console.log(`[CustomProduct] Found ${containers.length} instance(s)`);
  
  containers.forEach(container => {
    const sectionId = container.id.replace('cus-custom-subscription-', '');
    new CustomProductSubscription(sectionId);
  });
});